import React, { createContext, useReducer } from 'react';
import { IL10N } from '../../Common/l10n-keys';
import { ArticleTileContextActionType, ViewType } from '../../Common/enums';
import { ICurrencyInformation } from '../articleTileGrid/article-tile/article-tile.d';
import { IUrlController } from './UrlController';

export interface IArticleTileContext {
    l10n: IL10N;
    categoryName: string;
    cdnUrl: string;
    mspOrigin: string;
    isGlobal: boolean;
    isMobile: boolean;
    storeLastPurchaseLsKey: boolean;
    currencyInformation: ICurrencyInformation;
    urlController: IUrlController;
    culture: string;
    customerType: string;
    enableAISorting: boolean;
    viewType: ViewType;
    activeEsClass: string;
    notValidEsClasses: string[];
    validEsClasses: string[];
    deliveryTimeTooltip: string | null;
    setMspOrigin: (origin: string) => void;
    focusFirstTileInNewPage: () => void;
    setActiveEsClass: (esClass?: string, validEsClasses?: string[], notValidEsClasses?: string[]) => void;
    cleanUpEsClassFilter: () => void;
    saveLastPurchasedState: (tileId: string, pageNo: number, activeEsClass?: string) => void;
}

export const ArticleTileContext = createContext<IArticleTileContext>({
    l10n: {} as IL10N,
    categoryName: '',
    cdnUrl: '',
    mspOrigin: '',
    isGlobal: false,
    isMobile: false,
    storeLastPurchaseLsKey: true,
    currencyInformation: {} as ICurrencyInformation,
    urlController: {} as IUrlController,
    culture: '',
    customerType: '',
    enableAISorting: false,
    viewType: ViewType.Category,
    activeEsClass: '',
    notValidEsClasses: [],
    validEsClasses: [],
    deliveryTimeTooltip: null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMspOrigin: (origin: string) => { },
    focusFirstTileInNewPage: () => { },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setActiveEsClass: (esClass?: string, validEsClasses?: string[], notValidEsClasses?: string[]) => { },
    cleanUpEsClassFilter: () => { },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveLastPurchasedState: (tileId: string, pageNo: number, activeEsClass?: string) => { }
});

type ArticleTileContextState = {
    mspOrigin: string;
    activeEsClass?: string;
    validEsClasses?: string[];
    notValidEsClasses?: string[];
};

function catStateReducer(state: ArticleTileContextState, action) {
    if (action.type === ArticleTileContextActionType.SetMspOrigin) {
        return {
            ...state,
            mspOrigin: action.payload.mspOrigin,
        };
    }

    if (action.type === ArticleTileContextActionType.SetActiveEsClass) {
        return {
            ...state,
            activeEsClass: action.payload.activeEsClass,
            validEsClasses: action.payload.validEsClasses,
            notValidEsClasses: action.payload.notValidEsClasses
        };
    }

    if (action.type === ArticleTileContextActionType.CleanUpEsClassFilter) {
        return {
            ...state,
            activeEsClass: undefined,
            validEsClasses: undefined,
            notValidEsClasses: undefined
        };
    }

    return state;
}


export default function ArticleTileContextProvider(
    props: React.PropsWithChildren<{
        l10n: IL10N, cdnUrl: string,
        isGlobal?: boolean, isMobile?: boolean,
        currencyInformation?: ICurrencyInformation, categoryName?: string,
        urlController: IUrlController | undefined,
        culture?: string,
        customerType?: string,
        enableAISorting?: boolean,
        viewType?: ViewType,
        storeLastPurchaseLsKey?: boolean,
        deliveryTimeTooltip?: string | null,
    }>,) {

    const [catState, catStateDispatch] = useReducer(catStateReducer, { mspOrigin: '' });

    function setMspOrigin(origin: string) {
        catStateDispatch({ type: ArticleTileContextActionType.SetMspOrigin, payload: { mspOrigin: origin } });
    }

    function setActiveEsClass(esClass?: string, validEsClasses?: string[], notValidEsClasses?: string[]) {
        catStateDispatch({ type: ArticleTileContextActionType.SetActiveEsClass, 
            payload: { activeEsClass: esClass, validEsClasses, notValidEsClasses } });
    }

    function cleanUpEsClassFilter() {
        catStateDispatch({ type: ArticleTileContextActionType.CleanUpEsClassFilter, 
            payload: { activeEsClass: undefined, validEsClasses: undefined, notValidEsClasses: undefined }});
    }

    function focusFirstTileInNewPage() {

    }

    function saveLastPurchasedState(tileId: string, pageNo: number) {
        if(!props.storeLastPurchaseLsKey) // by default it is true, only regular fs tile should have it false
            return;

        localStorage.setItem('ats_lastPurchased', JSON.stringify({
            lpTileId: tileId,
            lastPage: pageNo,
            scrollPosition: window.scrollY,
            activeEsClass: catState.activeEsClass,
            validEsClasses: catState.validEsClasses,
            notValidEsClasses: catState.notValidEsClasses,
        }));
    }

    const ctxValue = {
        l10n: props.l10n,
        cdnUrl: props.cdnUrl,
        setMspOrigin: setMspOrigin,
        mspOrigin: catState.mspOrigin,
        setActiveEsClass: setActiveEsClass,
        cleanUpEsClassFilter: cleanUpEsClassFilter,
        activeEsClass: catState.activeEsClass,
        validEsClasses: catState.validEsClasses,
        notValidEsClasses: catState.notValidEsClasses,
        focusFirstTileInNewPage: focusFirstTileInNewPage,
        isGlobal: props.isGlobal ?? false,
        isMobile: props.isMobile ?? false,
        currencyInformation: props.currencyInformation ?? {} as ICurrencyInformation,
        categoryName: props.categoryName ?? '',
        urlController: props.urlController ?? {} as IUrlController,
        culture: props.culture ?? '',
        customerType: props.customerType ?? '',
        enableAISorting: props.enableAISorting ?? false,
        viewType: props.viewType ?? ViewType.Category,
        storeLastPurchaseLsKey: props.storeLastPurchaseLsKey ?? true,
        deliveryTimeTooltip: props.deliveryTimeTooltip ?? null,
        saveLastPurchasedState: saveLastPurchasedState
    };

    return (
        <ArticleTileContext.Provider value={ctxValue}>
            {props.children}
        </ArticleTileContext.Provider>
    );
}
